/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useRef, Suspense, lazy, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import MusicDial, { VIDEOS, VideoItem } from './MusicDial';
import LoadingScreen from './LoadingScreen';
import AudioVisualizer, { connectAudioStream, initAudio } from './AudioVisualizer';
import ErrorBoundary from './ErrorBoundary';

const ProfileUI = lazy(() => import('./ProfileUI'));
const GateSceneCanvas = lazy(() => import('./GateScene'));

interface BackgroundVideoProps {
  src: string;
  isActive: boolean;
  isIntro?: boolean;
  isMuted?: boolean;
  shouldLoop?: boolean;
  onTimeUpdate?: any;
  onEnded?: any;
  onLoadedData?: any;
}

const BackgroundVideo = React.forwardRef<HTMLVideoElement, BackgroundVideoProps>(
  ({ src, isActive, isIntro, isMuted = true, shouldLoop = true, onTimeUpdate, onEnded, onLoadedData }, externalRef) => {
    const isYouTube = src.includes('youtube.com') || src.includes('youtu.be');
    const isVimeo = src.includes('vimeo.com');
    const internalRef = useRef<HTMLVideoElement>(null);

    const setRefs = React.useCallback(
      (node: HTMLVideoElement) => {
        internalRef.current = node;
        if (typeof externalRef === 'function') {
          externalRef(node);
        } else if (externalRef) {
          (externalRef as React.MutableRefObject<HTMLVideoElement | null>).current = node;
        }
      },
      [externalRef]
    );

    useEffect(() => {
      const video = internalRef.current;
      if (video && !isYouTube && !isVimeo) {
        if (isActive) {
          video.currentTime = 0;
          video.play().catch(e => {
            if (e.name !== 'AbortError') console.error("Video play error:", e);
          });
        } else {
          // Pause slightly after fade-out to ensure smooth visual transition
          const timer = setTimeout(() => {
            if (internalRef.current) internalRef.current.pause();
          }, 1500);
          return () => clearTimeout(timer);
        }
      }
    }, [isActive, isYouTube, isVimeo, src]);

    if (isYouTube && isActive) {
      let videoId = '';
      if (src.includes('youtu.be/')) videoId = src.split('youtu.be/')[1].split('?')[0];
      else if (src.includes('v=')) videoId = src.split('v=')[1].split('&')[0];
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.5 }} className="absolute inset-0 w-full h-full pointer-events-none">
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${isMuted ? 1 : 0}&controls=0&showinfo=0&rel=0&loop=${shouldLoop ? 1 : 0}&playlist=${videoId}&modestbranding=1&playsinline=1`}
            className="absolute top-1/2 left-1/2 w-[300vw] h-[300vh] -translate-x-1/2 -translate-y-1/2 sm:w-[150vw] sm:h-[150vh]"
            allow="autoplay; encrypted-media"
            style={{ border: 'none' }}
          />
        </motion.div>
      );
    }

    if (isVimeo && isActive) {
      const videoId = src.split('vimeo.com/')[1].split('?')[0];
      return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.5 }} className="absolute inset-0 w-full h-full pointer-events-none">
          <iframe
            src={`https://player.vimeo.com/video/${videoId}?background=1&autoplay=1&loop=${shouldLoop ? 1 : 0}&byline=0&title=0&muted=${isMuted ? 1 : 0}`}
            className="absolute top-1/2 left-1/2 w-[300vw] h-[300vh] -translate-x-1/2 -translate-y-1/2 sm:w-[150vw] sm:h-[150vh]"
            allow="autoplay; fullscreen; picture-in-picture"
            style={{ border: 'none' }}
          />
        </motion.div>
      );
    }

    if (isYouTube || isVimeo) return null;

    // Fast HTML5 local Preloaded Video
    return (
      <video 
        ref={setRefs}
        src={src}
        preload="auto"
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1500ms] ease-in-out ${!isIntro ? 'scale-[1.35]' : 'scale-105'} ${isActive ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
        muted={isMuted}
        playsInline
        loop={shouldLoop}
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        onLoadedData={onLoadedData}
      >
        <track kind="captions" />
      </video>
    );
  }
);

// --- Intro Gate Components --- 
// Extracted to src/GateScene.tsx

// --- Main App ---

const PROFILE_FADE_IN_TIME = 18; // Configurable time in seconds before profile fades in
const LOADING_SKIP_TIME = 8000; // If video hasn't loaded after 8s, show skip button

export default function App() {
  const [stage, setStage] = useState<'gate' | 'transition' | 'profile'>('gate');
  const videoRef = useRef<HTMLVideoElement>(null);
  const glowCanvasRef = useRef<HTMLCanvasElement>(null);
  const [showUI, setShowUI] = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);
  const [showGreatnessText, setShowGreatnessText] = useState(false);
  const [activeVideo, setActiveVideo] = useState<VideoItem>(() => VIDEOS[Math.floor(Math.random() * VIDEOS.length)]);
  const [playingIntro, setPlayingIntro] = useState(true);
  const [preloadStage, setPreloadStage] = useState<'main' | 'active' | 'all'>('main');
  const isLoading = preloadStage === 'main';
  const [showSkipButton, setShowSkipButton] = useState(false);
  const musicVideoRef = useRef<HTMLVideoElement>(null);

  // Safety fallback for loading screen if video takes too long or triggers early
  useEffect(() => {
    const timer = setTimeout(() => {
      setPreloadStage(prev => prev === 'main' ? 'active' : prev);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  // Show skip button if loading screen persists beyond LOADING_SKIP_TIME
  useEffect(() => {
    if (!isLoading) {
      setShowSkipButton(false);
      return;
    }
    const timer = setTimeout(() => setShowSkipButton(true), LOADING_SKIP_TIME);
    return () => clearTimeout(timer);
  }, [isLoading]);

  const handleSkipLoading = useCallback(() => {
    setPreloadStage('all');
    setShowSkipButton(false);
  }, []);

  const handleMainLoaded = useCallback(() => {
    setPreloadStage(prev => prev === 'main' ? 'active' : prev);
  }, []);

  const handleActiveLoaded = useCallback(() => {
    setPreloadStage(prev => prev === 'active' ? 'all' : prev);
  }, []);

  // Run only when a video is actually playing (not during gate or after video ends)
  const currentSrc = playingIntro ? '/main.mp4' : activeVideo.src;
  const currentKey = playingIntro ? 'intro' : activeVideo.id;
  const shouldGlow = currentSrc !== '' && !videoEnded;

  useEffect(() => {
    if (!shouldGlow) return;

    let animationFrameId: number;
    let lastTime = 0;

    const updateGlow = (time: number) => {
      if (time - lastTime > 100) {
        const videoEl = document.querySelector('video.opacity-100') as HTMLVideoElement;
        if (videoEl && glowCanvasRef.current && !videoEl.paused && !videoEl.ended) {
          const ctx = glowCanvasRef.current.getContext('2d');
          if (ctx) {
            ctx.filter = videoEl.style.filter || 'none';
            ctx.drawImage(videoEl, 0, 0, 64, 64);
          }
        }
        lastTime = time;
      }
      animationFrameId = requestAnimationFrame(updateGlow);
    };

    animationFrameId = requestAnimationFrame(updateGlow);

    return () => cancelAnimationFrame(animationFrameId);
  }, [shouldGlow]);

  // Bind audio stream once when the music video element mounts
  useEffect(() => {
    if (musicVideoRef.current) {
      connectAudioStream(musicVideoRef.current);
    }
  }, []);

  const handleGateClick = () => {
    if (stage !== 'gate') return;
    setStage('transition');
    setShowGreatnessText(true);
    setShowUI(false);
    
    // Robust fullscreen request with vendor prefixes
    const docEl = document.documentElement as any;
    try {
      if (docEl.requestFullscreen) {
        docEl.requestFullscreen().catch((e: any) => console.error("Fullscreen failed:", e));
      } else if (docEl.webkitRequestFullscreen) {
        docEl.webkitRequestFullscreen();
      } else if (docEl.msRequestFullscreen) {
        docEl.msRequestFullscreen();
      }
    } catch (err) {
      console.error("Fullscreen API error:", err);
    }

    // Must trigger during a trusted user event (onclick) to satisfy Safari/Chrome AutoPlay laws
    initAudio(); 

    if (videoRef.current) {
      videoRef.current.muted = false;
      videoRef.current.currentTime = 0;
      const playPromise = videoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(e => {
          if (e.name !== 'AbortError') {
            console.error("Video play failed:", e);
          }
        });
      }
    }

    setTimeout(() => {
      setStage('profile');
    }, 1500);

    setTimeout(() => {
      setShowGreatnessText(false);
    }, 6000);
  };

  const handleTimeUpdate = (e: React.SyntheticEvent<HTMLVideoElement>) => {
    if (e.currentTarget.currentTime > PROFILE_FADE_IN_TIME) {
      setShowUI(true);
    }
  };

  const handleVideoEnded = () => {
    if (playingIntro) {
      setPlayingIntro(false);
      setShowUI(true);
    }
  };

  const handleVideoSelect = (video: VideoItem) => {
    setPlayingIntro(false);
    setActiveVideo(video);
    setVideoEnded(false);
    setShowUI(true);
  };

  return (
    <>
    {isLoading && showSkipButton && (
      <button
        onClick={handleSkipLoading}
        className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[10000] px-8 py-3 rounded-full bg-white/10 border border-white/20 text-white/60 text-xs tracking-widest uppercase hover:bg-white/20 hover:text-white/80 transition-all backdrop-blur-md"
        aria-label="Skip loading and enter now"
      >
        Skip &amp; Enter
      </button>
    )}
    <LoadingScreen isLoading={isLoading} />
    <div className="fixed inset-0 bg-[#050505] p-3 sm:p-6 md:p-8 flex items-center justify-center">
      {/* Ambient Glow Canvas */}
      <canvas
        ref={glowCanvasRef}
        width={64}
        height={64}
        className={`absolute inset-0 w-full h-full object-cover blur-[60px] sm:blur-[100px] scale-110 transition-opacity duration-1000 ${currentSrc !== '' && !videoEnded ? 'opacity-60' : 'opacity-0'}`}
      />

      {/* Main Frame */}
      <div className="relative w-full h-full rounded-[2rem] sm:rounded-[2.5rem] bg-black z-10 shadow-2xl border border-white/10">
        
        {/* Background Videos pre-mounted for instant load */}
        <div className={`absolute inset-0 z-0 transition-opacity duration-1000 bg-black overflow-hidden rounded-[2rem] sm:rounded-[2.5rem] ${stage !== 'gate' ? 'opacity-100' : 'opacity-[0.01]'}`}>
          <BackgroundVideo 
            ref={videoRef}
            src="/main.mp4"
            isActive={playingIntro && !videoEnded && stage !== 'gate'}
            isIntro={true}
            isMuted={stage === 'gate'}
            shouldLoop={stage === 'gate'}
            onTimeUpdate={playingIntro ? handleTimeUpdate : undefined}
            onEnded={playingIntro ? handleVideoEnded : undefined}
            onLoadedData={handleMainLoaded}
          />
          {(!playingIntro || videoEnded) && (
            <BackgroundVideo 
              ref={musicVideoRef}
              src={activeVideo.src}
              isActive={true}
              isIntro={false}
              isMuted={false}
              shouldLoop={true}
            />
          )}

          {/* Intelligent Preloader — only buffer neighboring videos */}
          <div style={{ display: 'none' }}>
            {preloadStage !== 'main' && (
              <video 
                src={activeVideo.src} 
                preload="auto" 
                muted 
                onLoadedData={handleActiveLoaded} 
              />
            )}
            {preloadStage === 'all' && (() => {
              const activeIdx = VIDEOS.findIndex(v => v.id === activeVideo.id);
              const nextIdx = (activeIdx + 1) % VIDEOS.length;
              const prevIdx = (activeIdx - 1 + VIDEOS.length) % VIDEOS.length;
              const toPreload = [VIDEOS[nextIdx], VIDEOS[prevIdx]];
              return toPreload.map(video => (
                <video key={video.id} src={video.src} preload="auto" muted />
              ));
            })()}
          </div>
          {/* Pixelated Filter Overlay */}
          <div 
            className="absolute inset-0 pointer-events-none z-10"
            style={{
              backgroundImage: 'linear-gradient(transparent 50%, rgba(0, 0, 0, 0.15) 50%), linear-gradient(90deg, transparent 50%, rgba(0, 0, 0, 0.15) 50%)',
              backgroundSize: '3px 3px'
            }}
          />
        </div>

        {/* 3D Gate */}
        <AnimatePresence>
          {stage !== 'profile' && (
            <motion.div 
              className="absolute inset-0 z-50 cursor-pointer flex items-center justify-center overflow-hidden rounded-[2rem] sm:rounded-[2.5rem]"
              onClick={handleGateClick}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleGateClick(); }}
              role="button"
              tabIndex={0}
              aria-label="Enter the experience"
              initial={{ backgroundColor: 'rgba(0,0,0,1)' }}
              animate={{ backgroundColor: stage === 'transition' ? 'rgba(0,0,0,0)' : 'rgba(0,0,0,1)' }}
              exit={{ opacity: 0 }}
              transition={{ duration: stage === 'transition' ? 1.5 : 0.5 }}
            >
              <Suspense fallback={
                <div className="flex items-center justify-center text-white/50 text-2xl font-bold tracking-widest">
                  THE GT
                </div>
              }>
                <GateSceneCanvas isTransitioning={stage === 'transition'} />
              </Suspense>
              
              {/* Vignette overlay during transition */}
              <motion.div 
                className="absolute inset-0 pointer-events-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: stage === 'transition' ? 1 : 0 }}
                style={{
                  background: 'radial-gradient(circle, transparent 20%, rgba(0,0,0,1) 100%)'
                }}
              />
              
              {stage === 'gate' && (
                <motion.div 
                  className="absolute bottom-10 text-white/50 text-sm tracking-widest uppercase font-medium"
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  Click to Enter
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Greatness Ahead Text */}
        <AnimatePresence>
          {showGreatnessText && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 1 }}
              className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 text-white/70 text-[11px] tracking-[0.3em] uppercase font-bold pointer-events-none"
              style={{ textShadow: '0 2px 10px rgba(0,0,0,0.8)' }}
            >
              Greatness Lies Ahead...
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Profile UI (Deferred until gate is clicked to optimize initial load) */}
        {stage !== 'gate' && (
          <ErrorBoundary>
          <div 
            className="absolute inset-0 z-10 overflow-y-auto overflow-x-hidden no-scrollbar flex flex-col rounded-[2rem] sm:rounded-[2.5rem]"
            style={{ pointerEvents: (stage === 'profile' && showUI) ? 'auto' : 'none' }}
          >
            <Suspense fallback={null}>
              <ProfileUI showUI={stage === 'profile' && showUI} />
            </Suspense>
          </div>
          </ErrorBoundary>
        )}
        
        {/* Music Selection Dial */}
        {stage === 'profile' && showUI && (
          <MusicDial onSelect={handleVideoSelect} activeVideo={activeVideo} />
        )}

        {/* Full Width Audio Visualizer Wave */}
        <AnimatePresence>
          {stage === 'profile' && showUI && (!playingIntro || videoEnded) && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1 }}
              className="absolute bottom-0 left-0 w-full z-20 pointer-events-none"
            >
              <AudioVisualizer />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
    </>
  );
}
